import { Component } from "react";
import { createRoot } from "react-dom/client";
import { unregister } from "./registerServiceWorker";

import "assets/css/components.css";
import "rsuite/dist/rsuite.min.css";
import "react-datepicker/dist/react-datepicker.css";
import "./assets/css/variantColors.scss";
import "./assets/css/index.css";
// router
import { BrowserRouter, Routes, Route } from "react-router-dom";
import indexRoutes from "./routes/Index";

// authentication
import { AmazonConnectApp } from "@amazon-connect/app";
import PrivateRoute from "./components/PrivateRoute";

// redux and api middleware
import apiMiddleware from "middlewares/TokenRefreshMiddleware";
import { Provider } from "react-redux";
import { applyMiddleware, combineReducers, createStore } from "redux";
import logger from "redux-logger";
import { persistReducer, persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import hardSet from "redux-persist/lib/stateReconciler/hardSet";
import storage from "redux-persist/lib/storage";
import { thunk } from "redux-thunk";

// reducers
import unlinkDeclinedFax from "components/UnlinkDeclinedFax/UnlinkDeclinedFax.slice";
import { Reducer as clientView } from "views/Admin/ClientView/ClientView.reducer";
import { Reducer as customerList } from "views/Admin/CustomerList/Customer.reducer";
import { Reducer as customerView } from "views/Admin/CustomerView/CustomerView.reducer";
import { Reducer as userList } from "views/Admin/UserList/User.reducer";
import { Reducer as callGroupDetails } from "views/AgentWorkspace/CallGroupDetails/CallGroupDetails.reducer";
import { Reducer as callGroupList } from "views/AgentWorkspace/CallGroupList/CallGroupList.reducer";
import { Reducer as swimSwitchList } from "views/AgentWorkspace/SwimSwitchList/SwimSwitchList.reducer";
import { Reducer as transcriberList } from "views/AgentWorkspace/TranscriberList/TranscriberList.reducer";
import { Reducer as batchList } from "views/Batch/BatchList/BatchList.reducer";
import { Reducer as batchScheduler } from "views/Batch/BatchScheduler/BatchScheduler.reducer";
import { blacklistedContactsReducer as blacklistedContactsList } from "views/BlacklistedContacts/BlacklistedContactsList/BlacklistedContactsList.reducer";
import { Reducer as codexVersion } from "views/Cat/CodexVersion/CodexVersion.reducer";
import { Reducer as deltaDynamicSwitchGroupList } from "views/Cat/CompareCodexVersion/UpdatedDynamicSwitchGroupList.reducer";
import { Reducer as drugSearch } from "views/Cat/DrugSearch/DrugSearch.reducer";
import { Reducer as dynamicSwitchGroup } from "views/Cat/DynamicSwitchGroup/DynamicSwitchGroup.reducer";
import { Reducer as dynamicSwitchGroupDetails } from "views/Cat/DynamicSwitchGroupDetails/DynamicSwitchGroupDetails.reducer";
import { Reducer as dynamicSwitchGroupList } from "views/Cat/DynamicSwitchGroupList/DynamicSwitchGroupList.reducer";
import { Reducer as cycleList } from "views/Cycle/CycleList/CycleList.reducer";
import { Reducer as cycleScheduler } from "views/Cycle/CycleScheduler/CycleScheduler.reducer";
import { Reducer as landing } from "views/Landing/Landing.reducer";
import { Reducer as prescribersProfileList } from "views/PrescribersProfileList/PrescribersProfileList.reducer";
import { Reducer as hscSwitchList } from "views/Switch/HscSwitchList/HscSwitchList.reducer";
import { Reducer as switchCycleList } from "views/Switch/SwitchCycleList/SwitchCycleList.reducer";
import { Reducer as switchDetails } from "views/Switch/SwitchDetails/SwitchDetails.reducer";
import { Reducer as switchList } from "views/Switch/SwitchList/SwitchList.reducer";
import { Reducer as unprocessedFax } from "views/Switch/UnprocessedFaxList/UnprocessedFaxList.reducer";
import { Reducer as dashboardMetrics } from "./components/DashboardMetrics/DashboardMetrics.reducer";
import switchActivities from "./components/MemberModule/ActivitiesPopup/ActivitiesPopup.slice";
import memberPrograms from "./components/MemberModule/CurrentPrograms/CurrentPrograms.slice";
import memberSwitches from "./components/MemberModule/MemberSwitchesGrid/MemberSwitchesGrid.slice";
import memberSpecificSwitchList from "./components/UnprocessedFaxList/MemberSwitchList.slice";
import tableOfContents from "./views/Analytics/TableOfContents/TableOfContents.slice";
import userFavoriteDashboards from "./views/Analytics/TableOfContents/UserFavoriteDashboards.slice";
import activeRadar from "./views/Cat/ActiveRadar/ActiveRadar.slice";
import { Reducer as allDrugList } from "./views/Cat/AllDrugs/AllDrugs.reducer";
import dsgWarningSlice from "./views/Cat/DSGWarning/DSGWarning.slice";
import { Reducer as newDrug } from "./views/Cat/NewDrug/NewDrug.reducer";
import { Reducer as memberDetail } from "./views/MemberProfile/MemberDetail/MemberDetail.reducer";
import memberList from "./views/MemberProfile/MemberList/MemberList.slice";
import drugGroup from "./views/Paw/DrugGroup/DrugGroup.slice";
import drugGroupDetail from "./views/Paw/DrugGroupDetails/DrugGroupDetails.slice";
import pawGroupDetail from "./views/Paw/PAWGroupDisplayBar/PAWGroupDisplayBar.slice";
import pawDsgs from "./views/Paw/PawDsgs/PawDsgs.slice";
import pawGroup from "./views/Paw/PawGroup/PawGroup.slice";
import rawList from "./views/Raw/RawList/RawList.slice";
import { Reducer as switchDisposition } from "./views/Raw/SwitchDisposition/SwitchDisposition.reducer";
import { Reducer as bulkActionList } from "./views/Schedular/BulkActionList/BulkActionList.reducer";
import { Reducer as bulkList } from "./views/Schedular/BulkList/BulkList.reducer";
import { Reducer as schedularForm } from "./views/Schedular/SchedularForm.reducer";

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["codexVersion"],
  stateReconciler: hardSet,
};

// combining all reducers
const appReducer = combineReducers({
  rawList,
  switchDisposition,
  dashboardMetrics,
  switchList,
  hscSwitchList,
  blacklistedContactsList,
  swimSwitchList,
  switchCycleList,
  batchList,
  switchDetails,
  batchScheduler,
  cycleList,
  cycleScheduler,
  landing,
  dynamicSwitchGroupList,
  codexVersion,
  drugSearch,
  unprocessedFax,
  dynamicSwitchGroupDetails,
  callGroupDetails,
  callGroupList,
  deltaDynamicSwitchGroupList,
  transcriberList,
  userList,
  dynamicSwitchGroup,
  activeRadar,
  pawGroupDetail,
  allDrugList,
  newDrug,
  pawGroup,
  drugGroup,
  drugGroupDetail,
  memberList,
  tableOfContents,
  userFavoriteDashboards,
  memberPrograms,
  switchActivities,
  memberSwitches,
  memberSpecificSwitchList,
  bulkActionList,
  schedularForm,
  bulkList,
  unlinkDeclinedFax,
  memberDetail,
  dsgWarningSlice,
  pawDsgs,
  prescribersProfileList,
  customerList,
  customerView,
  clientView,
});

// highest level reducer
const rootReducer = (state, action) => {
  if (action.type === "USER_LOGOUT") {
    state = undefined;
  }
  return appReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const middleware = [];

if (import.meta.env.DEV) {
  middleware.push(logger);
}

// configuring the store to save state
const stateStore = createStore(
  persistedReducer,
  applyMiddleware(apiMiddleware, thunk, ...middleware)
);

const persistor = persistStore(stateStore);

AmazonConnectApp.init({
  onCreate: (event) => {
    const { appInstanceId } = event.context;
    console.log("App initialized: ", appInstanceId);
  },
  onDestroy: (event) => {
    console.log("App being destroyed");
  },
});

const Root = ({ store }) => {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <BrowserRouter>
          <Routes>
            {indexRoutes.map((prop) =>
              prop.isProtected ? (
                <Route
                  key={prop.path}
                  path={prop.path}
                  element={<PrivateRoute component={prop.component} />}
                />
              ) : (
                <Route
                  key={prop.path}
                  path={prop.path}
                  element={<prop.component />}
                />
              )
            )}
          </Routes>
        </BrowserRouter>
      </PersistGate>
    </Provider>
  );
};

const container = document.getElementById("root");
const root = createRoot(container);
root.render(<Root store={stateStore} />);

unregister();
